import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  ActionIcon,
  Popover,
  RadioGroup,
  Radio,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { cobalt_blue } from "../../const/variables";

interface EditableTextWithOptionsProps {
  label: string;
  popoverLabel?: string;
  value: string;
  color?: string;
  options?: string[];
  /** Map từ option value → label hiển thị trong popup. Nếu không có thì dùng chính option */
  optionLabels?: Record<string, string>;
  onChange: (value: string) => void;
  width?: string;
  onClick?: () => void;
  isCod?: boolean;
  required?: boolean;
  readOnly?: boolean;
}

const EditableTextWithOptions: React.FC<EditableTextWithOptionsProps> = ({
  label,
  popoverLabel,
  value,
  options = [],
  optionLabels,
  onChange,
  color,
  width = "auto",
  onClick,
  isCod = false,
  required = false,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value);
  const [searchTerm, setSearchTerm] = useState(""); // State để lưu giá trị tìm kiếm

  const handleEditClick = () => {
    setIsEditing((prev) => !prev);
    setSearchTerm(""); // Reset giá trị tìm kiếm khi mở popover
  };

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
    onChange(value);
    setIsEditing(false);
  };

  const displayLabel = optionLabels?.[selectedOption] ?? selectedOption;

  const filteredOptions = options?.filter((option) => {
    const label = optionLabels?.[option] ?? option;
    return (
      option?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
  });

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  return (
    <Flex align="center" gap="xs" w={"100%"}>
      {label && (
        <Text style={{ flex: isCod ? "0 0 10px" : "0 0 100px" }}>
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </Text>
      )}
      <Tooltip
        label={`${displayLabel}`}
        arrowOffset={10}
        arrowSize={4}
        withArrow
        color="blue"
      >
        <Text
          fw={500}
          c={color}
          style={{ whiteSpace: "nowrap", pointerEvents: "auto" }}
          onClick={onClick}
          truncate
        >
          {displayLabel}
        </Text>
      </Tooltip>
      {!readOnly && (
        <Popover
          opened={isEditing}
          onClose={() => setIsEditing(false)}
          width={200}
          position="bottom"
          withArrow
          withinPortal
          onDismiss={() => setIsEditing(false)}
        >
          <Popover.Target>
            <ActionIcon size={18} variant="white" onClick={handleEditClick}>
              <IconEdit color={cobalt_blue.base} size={18} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown style={{ minWidth: width }}>
            <TextInput
              placeholder="Tìm kiếm..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.currentTarget.value)}
              mb={8}
            />
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              <RadioGroup
                value={selectedOption}
                onChange={handleOptionChange}
                label={popoverLabel ?? `Chọn ${label.toLowerCase()}`}
                required
              >
                {filteredOptions?.map((option, index) => (
                  <Radio
                    mt={5}
                    key={index}
                    value={option}
                    label={optionLabels?.[option] ?? option}
                  />
                ))}
              </RadioGroup>
            </div>
          </Popover.Dropdown>
        </Popover>
      )}
    </Flex>
  );
};

export default EditableTextWithOptions;
